import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { isCartAddPending, waitForPendingCartAdds } from '@theme/gullyo-cart-sync';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #pendingAddSync = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#preserveOpenOnCartUpdate);
    document.addEventListener('gullyo:cart-add-pending', this.#handleAddPendingStart);
    document.addEventListener('gullyo:cart-add-settled', this.#handleAddPendingSettled);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#preserveOpenOnCartUpdate);
    document.removeEventListener('gullyo:cart-add-pending', this.#handleAddPendingStart);
    document.removeEventListener('gullyo:cart-add-settled', this.#handleAddPendingSettled);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (event.detail?.data?.didError) return;

    if (typeof window.GullyoCartSync?.waitForPendingCartAdds === 'function') {
      await window.GullyoCartSync.waitForPendingCartAdds();
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!this.refs.dialog?.open) {
          this.showDialog();
        }
      });
    });
  };

  /**
   * @param {boolean} active
   */
  #setDrawerAddingState(active) {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.classList.toggle('cart-drawer--adding', active);
    dialog.toggleAttribute('aria-busy', active);

    if (active) {
      dialog.classList.remove('cart-drawer--empty');
      dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
    } else {
      dialog.removeAttribute('aria-busy');
    }
  }

  #syncDrawerWhileAddPending = async () => {
    if (!isCartAddPending()) return;
    if (this.#pendingAddSync) {
      await this.#pendingAddSync;
      return;
    }

    this.#pendingAddSync = (async () => {
      try {
        await waitForPendingCartAdds();
      } finally {
        this.#setDrawerAddingState(false);
        this.#pendingAddSync = null;
        this.#updateStickyState();
      }
    })();

    await this.#pendingAddSync;
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog?.open) return;

    if (isCartAddPending()) {
      this.#setDrawerAddingState(true);
    }

    super.showDialog();
    void this.#syncDrawerWhileAddPending();
  };

  #handleAddPendingStart = () => {
    if (!this.refs.dialog?.open) return;
    this.#setDrawerAddingState(true);
    void this.#syncDrawerWhileAddPending();
  };

  #handleAddPendingSettled = () => {
    if (!this.refs.dialog?.open) return;
    if (isCartAddPending()) return;
    this.#setDrawerAddingState(false);
    this.#updateStickyState();
  };

  /** @type {boolean} */
  #keepOpenDuringCartUpdate = false;

  #preserveOpenOnCartUpdate = (event) => {
    if (!this.refs.dialog?.open) return;
    if (event.detail?.data?.source !== 'cart-items-component') return;

    this.#keepOpenDuringCartUpdate = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.#keepOpenDuringCartUpdate && this.refs.dialog && !this.refs.dialog.open) {
          this.showDialog();
        }
        this.#keepOpenDuringCartUpdate = false;
        this.#updateStickyState();
      });
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
